import { useMemo } from 'react';
import type { RevenueMetrics } from 'services/types/relifDashboard';

import {
  calculateTotalLineData,
  filterAndSortMonths,
  formatMonthLabels,
  getMonthsWithData,
  transformIndustryDataForChart,
  transformMonthlyRevenueForBarChart,
} from '../utils/chartDataUtils';

interface UseRevenueDataReturn {
  monthlyRevenueData: Array<{ label: string; value: number }>;
  revenueByIndustryData: Array<{ industry: string; data: number[]; months: string[] }>;
  totalLineData: { industry: string; data: number[]; months: string[] } | null;
  monthLabels: string[];
}

export default function useRevenueData(
  revenueData: RevenueMetrics | undefined,
  selectedYear: number
): UseRevenueDataReturn {
  return useMemo(() => {
    if (!revenueData) {
      return {
        monthlyRevenueData: [],
        revenueByIndustryData: [],
        totalLineData: null,
        monthLabels: [],
      };
    }

    // Transform monthly revenue for bar chart
    const monthlyRevenueData = transformMonthlyRevenueForBarChart(revenueData.monthlyRevenue, selectedYear);

    // Get all months from revenue data
    const allMonths = new Set<string>();
    if (revenueData.revenueByIndustry) {
      Object.values(revenueData.revenueByIndustry).forEach((monthlyData) => {
        Object.keys(monthlyData).forEach((month) => {
          allMonths.add(month);
        });
      });
    }
    if (revenueData.monthlyRevenue) {
      Object.keys(revenueData.monthlyRevenue).forEach((month) => {
        allMonths.add(month);
      });
    }

    // Filter and sort months
    const sortedMonths = filterAndSortMonths(Array.from(allMonths), selectedYear);

    // Get months with data
    const sortedMonthsWithData = getMonthsWithData(revenueData.revenueByIndustry, sortedMonths);

    // Transform industry data for line chart
    const revenueByIndustryData = transformIndustryDataForChart(
      revenueData.revenueByIndustry,
      sortedMonthsWithData,
      true
    );

    // Calculate total line data
    const totalLineData = calculateTotalLineData(revenueByIndustryData, sortedMonthsWithData);

    // Format month labels
    const monthLabels = formatMonthLabels(
      revenueByIndustryData.length > 0 || totalLineData ? (revenueByIndustryData[0] || totalLineData)?.months || [] : []
    );

    return {
      monthlyRevenueData,
      revenueByIndustryData,
      totalLineData,
      monthLabels,
    };
  }, [revenueData, selectedYear]);
}
